import { Close } from "@mui/icons-material";
import { Alert, Box, Dropdown, Menu, MenuButton, MenuItem, Stack, Typography } from "@mui/joy";
import Color from "color";
import { format } from "date-fns";
import { ApiTask, TaskStatus } from "griptonite-api-client/types/tasks";
import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Route, Task } from "@/core/types";
import { useCurrentVenueCircuitLookup, useVenueZonesByClimbType } from "@/hooks/routes";

import CheckboxInput from "../forms/CheckboxInput";
import CircuitSelect, { CompetitionSelect } from "../forms/CircuitSelect";
import { ColorsFormInput } from "../forms/ColorsSelect";
import { DifficultyInput } from "../forms/DifficultyInput";
import { GradePickerFormInput } from "../forms/GradePicker";
import LineSelect from "../forms/LineSelect";
import {
  LineSelectionMapInput,
  MapPositionInput,
  RouteSelectionMapInput,
} from "../forms/MapPositionInput";
import { PillsSelectInput } from "../forms/PillsSelect";
import StaffSelect from "../forms/StaffSelect";
import { TagScanInput } from "../forms/TagInput";
import TextInput, {
  DateInput,
  HeightInputAutoUnits,
  NumberInput,
  TextareaInput,
} from "../forms/TextInput";
import { TraitSelectorFormInput } from "../forms/TraitSelector";
import { IconButtonLink } from "../Links";
import {
  CompetitionCategoriesSelect,
  FormSection,
  RouteNumberField,
  useTakenNumbers,
  useUpdateRouteGroupsWhenCompetitionChanges,
} from "../RouteForm/RouteFormFields";
import { RouteNameText } from "../RoutesTable/RouteName";
import StatusChip from "../TaskCard/StatusChip";
import { CloseButton } from "../TaskDetailSidebar/CloseButton";
import { useTaskSetOverview } from "../TaskSetOverview/useTaskSetOverview";
import { useTaskStatusChange } from "../TaskSetOverview/useTaskStatusChange";

const TaskHeader = ({ task }: { task?: Task }) => {
  const { watch } = useFormContext();
  const route = watch("route");
  const colors = (watch("route.colors") || "#FFFFFF").split(":");
  console.log({ colors });
  const bgColor = colors[0];
  const fgColor = Color(bgColor).isDark() ? "#FFFFFF" : "#000000";

  const changeStatus = useTaskStatusChange();

  const onChangeStatus = (status: TaskStatus) => {
    task && changeStatus(task, status);
  };

  return (
    <Box
      sx={{
        marginTop: task?.route ? "-16px" : 0,
        marginLeft: "-1px",
        height: 42,
        backgroundColor: bgColor,
        color: fgColor,
        padding: 1,
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid",
        borderColor: "#00000033",
        borderRadius: "16px 16px 0 0",
        position: "relative",
        zIndex: 1,
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {task?.route ? <RouteNameText route={route} /> : task?.name}
      </Typography>
      <Stack direction={"row"} spacing={1} alignItems="center">
        <Dropdown>
          <MenuButton
            slots={{ root: Box }}
            slotProps={{
              root: {
                sx: { cursor: "pointer" },
              },
            }}
          >
            {task && <StatusChip status={task.status} />}
          </MenuButton>
          <Menu size="sm">
            <MenuItem onClick={() => onChangeStatus("TODO")}>
              <StatusChip status="TODO" />
            </MenuItem>
            <MenuItem onClick={() => onChangeStatus("IN_PROGRESS")}>
              <StatusChip status="IN_PROGRESS" />
            </MenuItem>
            <MenuItem onClick={() => onChangeStatus("DONE")}>
              <StatusChip status="DONE" />
            </MenuItem>
          </Menu>
        </Dropdown>
        {task && !task?.route ? (
          <IconButtonLink
            to={"/gym/$venueUuid/schedule/set/$setUuid"}
            sx={{ borderRadius: "100%" }}
            params={{
              venueUuid: task.venue,
              setUuid: task.set,
            }}
          >
            <Close sx={{ color: fgColor }} />
          </IconButtonLink>
        ) : null}
      </Stack>
    </Box>
  );
};

export const RouteSetFields = ({ task }: { task: ApiTask }) => {
  const circuit = useCurrentVenueCircuitLookup(task?.route?.circuit?.uuid);
  const chosenCircuit = useCurrentVenueCircuitLookup(
    useWatch({ name: "route.circuit.uuid" }) || undefined,
  );
  const competitionUuid = useWatch({ name: "route.competition" });
  const { taskSet } = useTaskSetOverview();
  const { data: zones } = useVenueZonesByClimbType(task?.venue, taskSet?.climbType ?? undefined);

  useUpdateRouteGroupsWhenCompetitionChanges("route");

  const circuitUuid = useWatch({ name: "circuit.uuid" });
  const competitionRouteGroupUuids = useWatch({
    name: "competitionRouteGroups",
  });

  const takenNumbers = useTakenNumbers({
    circuitUuid,
    competitionRouteGroupUuids,
    currentNumber: task.route?.circuitNumber,
  });

  if (!task.route || task.type !== "SET") {
    return null;
  }

  return (
    <>
      {task.route.climbType === "SPORT" ? (
        <FormSection>
          {!taskSet?.competition ? <LineSelect route={task.route} name="route.startLine" /> : null}
          <TagScanInput name="tag" prefix="route" />
          {!taskSet?.competition ? <CompetitionSelect name="route.competition" /> : null}
        </FormSection>
      ) : (
        <>
          {!taskSet?.competition ? (
            <FormSection>
              <CircuitSelect
                name="route.circuit.uuid"
                competitionFieldName="route.competition"
                withCompetitions
              />
            </FormSection>
          ) : null}
          {competitionUuid ? (
            <FormSection>
              <CompetitionCategoriesSelect
                name="route.competitionRouteGroups"
                competitionUuid={competitionUuid}
              />
            </FormSection>
          ) : null}
          <FormSection>
            <TagScanInput name="tag" prefix="route" />
          </FormSection>
          <RouteNumberField
            key={task.route.uuid}
            name="route.circuit.number"
            route={task.route}
            routeDisplay={chosenCircuit?.routeDisplay}
            takenNumbers={takenNumbers}
          />
          {circuit?.gradesEstimated ? (
            <FormSection>
              <DifficultyInput
                name="route.circuit.difficulty"
                circuitUuidName="route.circuit.uuid"
                gradeEstimatedName="route.gradeEstimated"
              />
            </FormSection>
          ) : null}
        </>
      )}
      {!zones || zones.length > 0 ? (
        <FormSection>
          <PillsSelectInput
            label={taskSet?.climbType === "SPORT" ? "Sectors" : "Zones"}
            name="route.zone"
            options={
              zones?.map((zone) => ({
                name: zone.name,
                value: zone.uuid,
              })) || []
            }
            // emptyWarning="Zone is missing"
            emptyWarning={taskSet?.climbType === "SPORT" ? "Sector is missing" : "Zone is missing"}
          />
        </FormSection>
      ) : null}
      <FormSection>
        <GradePickerFormInput
          label="Grade"
          name="route.gradeValue"
          gradeSystemName="route.gradeSystem"
          climbType={task.route.climbType}
        />
      </FormSection>
      <FormSection>
        <Stack direction={"row"} spacing={1}>
          <ColorsFormInput label="Hold colors" name="route.colors" />
        </Stack>
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Styles" traitType="styles" name="route.styles" />
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Skills" traitType="skills" name="route.skills" />
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Holds" traitType="holds" name="route.holds" />
      </FormSection>
      {task.route?.climbType === "SPORT" ? (
        <>
          <FormSection>
            <CheckboxInput name="route.autoBelay" label="Has autobelay?" />
          </FormSection>
          <FormSection>
            <NumberInput name="route.quickdrawCount" label="Quickdraws" />
          </FormSection>
          <FormSection>
            <HeightInputAutoUnits name="route.height" label="Height" />
          </FormSection>
        </>
      ) : null}
      <FormSection>
        <TextInput name="route.name" label="Name" />
      </FormSection>
      <FormSection>
        <TextareaInput name="route.description" label="Description" />
      </FormSection>
      <FormSection>
        <StaffSelect label="Setters" name="route.setters" onlyActiveStaff multiple />
      </FormSection>
      <FormSection>
        <DateInput
          name="route.scheduledEndTime"
          label="Scheduled end date"
          withTime={false}
          asDateTime
          defaultTime="12:00:00"
          minDate={format(new Date(), "yyyy-MM-dd")}
        />
      </FormSection>
    </>
  );
};

const RouteStripFields = ({ task }: { task: ApiTask }) => {
  const routeUuid = useWatch({ name: "routeUuid" });
  console.log({ routeUuid });
  if (task.type !== "STRIP") {
    return null;
  }
  if (!task.route) {
    if (!routeUuid) {
      return (
        <>
          <FormSection>
            <Alert color="warning">Select a route on the map to be stripped</Alert>
          </FormSection>
          <FormSection>
            <StaffSelect label="Assignees" name="assignees" onlyActiveStaff multiple />
          </FormSection>
        </>
      );
    }
    return null;
  }
  return (
    <>
      {task.route.climbType === "SPORT" ? (
        <FormSection>
          <LineSelect route={task.route} name="route.startLine" disabled />
        </FormSection>
      ) : (
        <>
          <FormSection>
            <CircuitSelect name="route.circuit.uuid" disabled />
          </FormSection>
        </>
      )}
      <FormSection>
        <StaffSelect label="Assignees" name="assignees" onlyActiveStaff multiple />
      </FormSection>
    </>
  );
};

const MiscTaskFields = ({ task }: { task?: ApiTask }) => {
  if (task?.type !== "MISC") {
    return null;
  }
  return (
    <>
      <FormSection>
        <TextInput name="name" label="Name" />
      </FormSection>
      <FormSection>
        <NumberInput name="allocatedDuration" label="Allocated time (mins)" />
      </FormSection>
      <FormSection>
        <StaffSelect label="Assignees" name="assignees" onlyActiveStaff multiple />
      </FormSection>
      <FormSection>
        <TextareaInput name="notes" label="Notes" helperText="Only visible to staff" minRows={3} />
      </FormSection>
    </>
  );
};

export const TaskForm = ({ task }: { task?: Task }) => {
  const { taskSet } = useTaskSetOverview();

  const routesFilterFn = useCallback(
    (route: Route) => {
      if (taskSet?.zone) {
        return route.zone === taskSet.zone;
      } else if (taskSet?.circuit) {
        return route.circuit?.uuid === taskSet.circuit.uuid;
      }
      return true;
    },
    [taskSet],
  );

  if (task?.type === "MISC" || !task) {
    return (
      <>
        <TaskHeader task={task} />
        <MiscTaskFields task={task} />
      </>
    );
  }

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <CloseButton />
      </Box>
      {task.route && task.route.climbType !== "SPORT" ? (
        <MapPositionInput
          prefix="route"
          route={task.route}
          filterPredicate={routesFilterFn}
          disabled={task.type === "STRIP"}
          key={task.uuid}
        />
      ) : null}
      {task.route && task.route.climbType === "SPORT" ? (
        <LineSelectionMapInput name="route.startLine" />
      ) : null}
      {!task.route ? (
        <RouteSelectionMapInput name="routeUuid" climbType={taskSet?.climbType || "BOULDER"} />
      ) : null}
      {task.route && task.type === "STRIP" ? <TaskHeader task={task} /> : null}
      {task.type === "SET" ? <RouteSetFields task={task} /> : null}
      {task.type === "STRIP" ? <RouteStripFields task={task} /> : null}
      <FormSection>
        <TextareaInput
          name="notes"
          label="Task notes"
          helperText="Only visible to other staff"
          minRows={3}
        />
      </FormSection>
    </>
  );
};

export default TaskForm;
